import {
  ChatCircleText,
  BellRinging,
  CalendarDots,
  DeviceMobile,
  CaretDown,
  CheckSquare,
  DeviceTabletSpeaker,
  GraduationCap,
  LockKey,
  ShoppingCart,
  SignOut,
  TextAa,
  Sparkle,
  Gift,
  UsersThree,
  type Icon as PhosphorIcon,
  Translate,
  SealCheck,
} from '@phosphor-icons/react';
import { Button, Icon, Text, buttonStyles, cn } from '@shh/ui';
import { signOut } from 'firebase/auth';
import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { firebase } from '../lib/firebase';
import { useWide } from '../lib/useWide';
import { useLens } from '../app/lens';
import { LensSheet } from '../app/LensSheet';
import { AiSettings } from './settings/AiSettings';
import { CalendarSettings } from './settings/CalendarSettings';
import { WhatsNewSheet } from '../components/WhatsNew';
import { DeviceModeNote, FamilyModeHint } from '../components/FamilyModeInfo';
import { feedbackText } from '../i18n/feedback';
import { feedbackLink } from './FeedbackPage';
import { APP_VERSION, shortVersion } from '../lib/releases';
import { DeviceSettings } from './settings/DeviceSettings';
import { DisplaySettings } from './settings/DisplaySettings';
import { FamilySettings } from './settings/FamilySettings';
import { FamilyDataSettings, HomeDetails } from './settings/HouseholdSettings';
import { LanguageSettings } from './settings/LanguageSettings';
import { PlusSectionHint, PlusSettings } from './settings/PlusSettings';
import { plusText } from '../i18n/plus';
import { ReminderSettings, ThisDeviceSettings } from './settings/ReminderSettings';
import { ShoppingSettings } from './settings/ShoppingSettings';
import { RewardSettings, TaskSettings } from './settings/TaskSettings';
import { PlanSettings } from './settings/PlanSettings';
import { settingsText } from '../i18n/settings';
import { languageText } from '../i18n/language';

const SECTIONS: {
  id: string;
  title: string;
  hint: ReactNode;
  icon: PhosphorIcon;
  body: () => ReactNode;
}[] = [
  // one place for the language (Mike, 14.10.): this device or the whole family
  {
    id: 'sprache',
    title: languageText.title,
    hint: languageText.hint,
    icon: Translate,
    body: () => <LanguageSettings />,
  },
  // Larovi Plus (1.21): status and what belongs to it – near the top, it concerns the whole family
  {
    id: 'plus',
    title: plusText.section.title,
    hint: <PlusSectionHint />,
    icon: SealCheck,
    body: () => <PlusSettings />,
  },
  {
    id: 'ki',
    title: settingsText.sections['ki'].title,
    hint: settingsText.sections['ki'].hint,
    icon: Sparkle,
    body: () => <AiSettings />,
  },
  {
    id: 'familie',
    title: settingsText.sections['familie'].title,
    hint: settingsText.sections['familie'].hint,
    icon: UsersThree,
    // "Zuhause" lives here since 1.17 (Mike, 11.10.): name and town on top, data at the bottom
    body: () => (
      <div className="flex flex-col gap-section">
        <HomeDetails />
        <FamilySettings />
        <FamilyDataSettings />
        <FamilyModeHint />
      </div>
    ),
  },
  {
    id: 'aufgaben',
    title: settingsText.sections['aufgaben'].title,
    hint: settingsText.sections['aufgaben'].hint,
    icon: CheckSquare,
    body: () => <TaskSettings />,
  },
  {
    id: 'belohnungen',
    title: settingsText.sections['belohnungen'].title,
    hint: settingsText.sections['belohnungen'].hint,
    icon: Gift,
    body: () => <RewardSettings />,
  },
  {
    id: 'kalender',
    title: settingsText.sections['kalender'].title,
    hint: settingsText.sections['kalender'].hint,
    icon: CalendarDots,
    body: () => <CalendarSettings />,
  },
  {
    id: 'erinnerungen',
    title: settingsText.sections['erinnerungen'].title,
    hint: settingsText.sections['erinnerungen'].hint,
    icon: BellRinging,
    body: () => <ReminderSettings />,
  },
  {
    id: 'einkauf',
    title: settingsText.sections['einkauf'].title,
    hint: settingsText.sections['einkauf'].hint,
    icon: ShoppingCart,
    body: () => <ShoppingSettings />,
  },
  {
    id: 'stundenplan',
    title: settingsText.sections['stundenplan'].title,
    hint: settingsText.sections['stundenplan'].hint,
    icon: GraduationCap,
    body: () => <PlanSettings />,
  },
  {
    id: 'geraete',
    title: settingsText.sections['geraete'].title,
    hint: settingsText.sections['geraete'].hint,
    icon: DeviceTabletSpeaker,
    body: () => <DeviceSettings />,
  },
  {
    id: 'dieses-geraet',
    title: settingsText.sections['dieses-geraet'].title,
    hint: settingsText.sections['dieses-geraet'].hint,
    icon: DeviceMobile,
    body: () => <ThisDeviceSettings />,
  },
  {
    id: 'anzeige',
    title: settingsText.sections['anzeige'].title,
    hint: settingsText.sections['anzeige'].hint,
    icon: TextAa,
    body: () => <DisplaySettings />,
  },
];

/** Old links to sections that moved: "Zuhause" is part of "Familie" since 1.17. */
const alias = (id: string) => (id === 'zuhause' ? 'familie' : id);

/** Settings: one page, clear sections that open in place – nothing hidden in sub-sub-menus. */
export function SettingsPage() {
  const lens = useLens();
  // /einstellungen#kalender opens that section – also when following a link on this page
  const { hash } = useLocation();
  const wide = useWide();
  const [picked, setPicked] = useState<{ hash: string; id: string | null }>();
  const open = picked && picked.hash === hash ? picked.id : alias(hash.slice(1)) || null;
  const setOpen = (id: string | null) => {
    setPicked({ hash, id });
    // desktop and wall: the chosen section starts at its top, not where the last one was scrolled
    if (wide) window.scrollTo({ top: 0 });
  };
  const [notesOpen, setNotesOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);

  if (!lens.canParent) {
    return (
      <div className="flex flex-col items-start gap-gap-md">
        <Icon icon={LockKey} size="xl" className="text-text-muted" />
        <Text>{settingsText.parentsOnly}</Text>
        {/* the wall: the parent PIN opens them right here */}
        {lens.isWall && (
          <>
            <Button icon={LockKey} onPress={() => setUnlockOpen(true)}>
              {settingsText.openWithPin}
            </Button>
            <LensSheet isOpen={unlockOpen} onOpenChange={setUnlockOpen} forSettings />
          </>
        )}
      </div>
    );
  }

  const whatsNew = (
    <>
      <Button
        variant="quiet"
        icon={Sparkle}
        className="self-start"
        onPress={() => setNotesOpen(true)}
      >
        {settingsText.whatsNew(shortVersion(APP_VERSION))}
      </Button>
      <WhatsNewSheet isOpen={notesOpen} onClose={() => setNotesOpen(false)} />
      <Link
        {...feedbackLink('/einstellungen')}
        className={cn(buttonStyles({ variant: 'quiet' }), 'self-start')}
      >
        <Icon icon={ChatCircleText} size="md" /> {feedbackText.entry}
      </Link>
    </>
  );
  const modeNote = <DeviceModeNote onOpen={() => setOpen('geraete')} />;
  const logout = !lens.isWall && (
    <Button
      variant="quiet"
      icon={SignOut}
      className="mt-gap-md self-start"
      onPress={() => void signOut(firebase().auth)}
    >
      {settingsText.signOut}
    </Button>
  );

  // Desktop (Mike, 07.10.): the list on the left, the chosen section on the right – like the
  // system settings of a computer; on phones the sections keep opening in place.
  if (wide) {
    const current = SECTIONS.find((s) => s.id === open) ?? SECTIONS[0]!;
    return (
      <div className="mx-auto flex w-full max-w-default items-start gap-gap-xl">
        <nav
          aria-label={settingsText.nav}
          className="sticky top-gap-lg flex w-80 shrink-0 flex-col gap-gap-xs"
        >
          {modeNote}
          {SECTIONS.map((s) => {
            const active = s.id === current.id;
            return (
              <button
                key={s.id}
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => setOpen(s.id)}
                className={cn(
                  'flex min-h-touch w-full cursor-pointer items-center gap-gap-md rounded-card p-inset-sm text-left outline-none focus-visible:outline-3 focus-visible:outline-border-focus',
                  active ? 'bg-surface-raised shadow-card' : 'hover:bg-action-quiet-hover',
                )}
              >
                <Icon icon={s.icon} size="lg" weight="duotone" className="text-accent-primary" />
                <span className="flex flex-1 flex-col">
                  <span className="font-display text-md font-black">{s.title}</span>
                  <span className="text-sm text-text-secondary">{s.hint}</span>
                </span>
              </button>
            );
          })}
          <div className="mt-gap-md flex flex-col">
            {whatsNew}
            {logout}
          </div>
        </nav>
        <section
          id={current.id}
          aria-labelledby={`${current.id}-title`}
          className="flex min-w-0 flex-1 flex-col gap-gap-md rounded-card bg-surface-raised p-inset-md shadow-card"
        >
          <h2 id={`${current.id}-title`} className="flex items-center gap-gap-sm">
            <Icon icon={current.icon} size="lg" weight="duotone" className="text-accent-primary" />
            <span className="font-display text-xl font-black">{current.title}</span>
          </h2>
          {current.body()}
        </section>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-narrow flex-col gap-gap-sm">
      {modeNote}
      {whatsNew}
      {SECTIONS.map((s) => {
        const isOpen = open === s.id;
        return (
          <section key={s.id} id={s.id} className="rounded-card bg-surface-raised shadow-card">
            <h2>
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={`${s.id}-body`}
                onClick={() => {
                  setOpen(isOpen ? null : s.id);
                }}
                className="flex min-h-touch w-full cursor-pointer items-center gap-gap-md rounded-card p-inset-sm text-left outline-none focus-visible:outline-3 focus-visible:outline-border-focus"
              >
                <Icon icon={s.icon} size="lg" weight="duotone" className="text-accent-primary" />
                <span className="flex flex-1 flex-col">
                  <span className="font-display text-lg font-black">{s.title}</span>
                  <span className="text-sm text-text-secondary">{s.hint}</span>
                </span>
                <Icon
                  icon={CaretDown}
                  size="sm"
                  className={isOpen ? 'rotate-180 text-text-muted' : 'text-text-muted'}
                />
              </button>
            </h2>
            {isOpen && (
              <div id={`${s.id}-body`} className="border-t border-border-subtle p-inset-sm">
                {s.body()}
              </div>
            )}
          </section>
        );
      })}
      {logout}
    </div>
  );
}
